import {
    Button,
    Group,
    Modal,
    NumberInput,
    Select,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core'
import {useEffect, useState} from 'react'

import {formatPrice} from '../lib/format-price'
import {parseNumberInputValueOr} from '../lib/parse-number-input-value'
import {chargeTotal, chargeUnitOptions, normalizeChargeUnit} from './financial-line-item-form-utils'
import type {JobDataLayer} from './job-data-layer'
import type {FinancialLineItemInput, JobDetail, JobDetailFinancialLineItem} from './types'

export type FinancialLineItemFormDraft = FinancialLineItemInput

type FinancialLineItemModalProps = {
    opened: boolean
    lineItem?: JobDetailFinancialLineItem
    initialDraft?: FinancialLineItemFormDraft
    job: JobDetail
    jobDataLayer: JobDataLayer
    onClose: () => void
    onJobUpdated: (job: JobDetail) => void
}

function emptyDraft(): FinancialLineItemFormDraft {
    return {
        name: '',
        description: '',
        quantity: 1,
        unit: 'each',
        unitPrice: 0,
    }
}

function draftFromLineItem(lineItem: JobDetailFinancialLineItem): FinancialLineItemFormDraft {
    return {
        chargeType: lineItem.chargeType,
        name: lineItem.name,
        description: lineItem.description,
        quantity: lineItem.quantity,
        unit: normalizeChargeUnit(lineItem.unit),
        unitPrice: lineItem.unitPrice,
    }
}

export function FinancialLineItemModal({
    opened,
    lineItem,
    initialDraft,
    job,
    jobDataLayer,
    onClose,
    onJobUpdated,
}: FinancialLineItemModalProps) {
    const isEditing = lineItem != null
    const [draft, setDraft] = useState<FinancialLineItemFormDraft>(emptyDraft())
    const [saving, setSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    useEffect(() => {
        if (!opened) {
            return
        }

        if (lineItem) {
            setDraft(draftFromLineItem(lineItem))
        } else if (initialDraft) {
            setDraft({...emptyDraft(), ...initialDraft})
        } else {
            setDraft(emptyDraft())
        }
        setErrorMessage(null)
    }, [opened, lineItem, initialDraft])

    async function handleSave() {
        const trimmedName = draft.name.trim()
        if (!trimmedName) {
            setErrorMessage('Description is required')
            return
        }

        if (draft.quantity <= 0) {
            setErrorMessage('Quantity must be greater than zero')
            return
        }

        if (draft.unitPrice <= 0) {
            setErrorMessage('Rate must be greater than zero')
            return
        }

        const input: FinancialLineItemInput = {
            ...draft,
            name: trimmedName,
            description: draft.description.trim(),
        }

        setSaving(true)
        setErrorMessage(null)
        try {
            const updatedJob = isEditing
                ? await jobDataLayer.updateFinancialLineItem(job.id, lineItem!.id, input)
                : await jobDataLayer.createFinancialLineItem(job.id, input)
            onJobUpdated(updatedJob)
            onClose()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save charge')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal opened={opened} onClose={onClose} title={isEditing ? 'Edit charge' : 'Add charge'}>
            <Stack gap="md">
                <TextInput
                    label="Description"
                    description="What the customer is charged for"
                    placeholder="e.g. Fuel tank, Diesel, Washout"
                    value={draft.name}
                    onChange={(event) => {
                        const name = event.currentTarget.value
                        setDraft((current) => ({...current, name}))
                    }}
                    required
                />
                <SimpleGrid cols={3}>
                    <NumberInput
                        label="Quantity"
                        min={0}
                        decimalScale={2}
                        value={draft.quantity}
                        onChange={(value) =>
                            setDraft((current) => ({
                                ...current,
                                quantity: parseNumberInputValueOr(value, 0),
                            }))
                        }
                    />
                    <Select
                        label="Unit"
                        data={chargeUnitOptions(draft.unit)}
                        value={draft.unit}
                        allowDeselect={false}
                        onChange={(value) => {
                            if (value) {
                                setDraft((current) => ({...current, unit: value}))
                            }
                        }}
                    />
                    <NumberInput
                        label={draft.unit === 'each' ? 'Rate (each)' : `Rate per ${draft.unit}`}
                        min={0}
                        prefix="$"
                        decimalScale={4}
                        value={draft.unitPrice}
                        onChange={(value) =>
                            setDraft((current) => ({
                                ...current,
                                unitPrice: parseNumberInputValueOr(value, 0),
                            }))
                        }
                    />
                </SimpleGrid>
                <Text size="sm">
                    Total: <b>{formatPrice(chargeTotal(draft.quantity, draft.unitPrice))}</b>
                </Text>
                <Text size="xs" c="dimmed">
                    One charge per rate, e.g. monthly or daily
                </Text>
                <Textarea
                    label="Note (optional)"
                    value={draft.description}
                    onChange={(event) => {
                        const description = event.currentTarget.value
                        setDraft((current) => ({...current, description}))
                    }}
                    minRows={2}
                />
                {errorMessage && (
                    <Text size="sm" c="red" role="alert">
                        {errorMessage}
                    </Text>
                )}
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={() => void handleSave()} loading={saving}>
                        {isEditing ? 'Save changes' : 'Add charge'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}
